import { Box, debounce, TextField, Card, CardContent, Typography, Button } from '@mui/material'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import { Autocomplete, GoogleMap, InfoWindow, Marker, useLoadScript } from '@react-google-maps/api'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { store } from '../../constants/store.js'
import LocationSearchingIcon from '@mui/icons-material/LocationSearching'

//This function takes in latitude and longitude of two location and returns the distance between them as the crow flies (in km)
function calcCrow(lat1, lon1, lat2, lon2) {
  var R = 6371 // km
  var dLat = toRad(lat2 - lat1)
  var dLon = toRad(lon2 - lon1)
  var lat1 = toRad(lat1)
  var lat2 = toRad(lat2)

  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2)
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  var d = R * c
  return d
}

// Converts numeric degrees to radians
function toRad(Value) {
  return (Value * Math.PI) / 180
}

const MARKERS = store
const center = {
  lat: 3.140853,
  lng: 101.693207,
}
const libraries = ['places']

export const Location = () => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  })

  const [activeMarker, setActiveMarker] = useState(null)
  const [markerInBounds, setMarkerInBounds] = useState([])
  const [myPos, setMyPos] = useState(null)
  const autoCompleteRef = useRef(null)
  const mapRef = useRef(null)
  const [searchLatLng, setSearchLatLng] = useState(null)

  const onLoadAutoComplete = (autocomplete) => {
    autoCompleteRef.current = autocomplete
  }
  const onLoadMap = (map) => {
    mapRef.current = map
  }

  const onPlaceChanged = () => {
    if (autoCompleteRef?.current) {
      const place = autoCompleteRef.current.getPlace()
      const bounds = new window.google.maps.LatLngBounds()

      setSearchLatLng({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      })

      if (place.geometry.viewport) {
        // Only geocodes have viewport.
        bounds.union(place.geometry.viewport)
      } else {
        bounds.extend(place.geometry.location)
      }
      mapRef?.current?.fitBounds(bounds)
    } else {
      console.log('Autocomplete is not loaded yet!')
    }
  }

  const onBoundsChanged = useCallback(
    debounce(() => {
      const latlngbounds = mapRef?.current?.getBounds()
      if (latlngbounds) {
        const tmp = MARKERS.filter((each) =>
          latlngbounds?.contains({ lat: each.lat, lng: each.lng })
        )
          .map((each) => {
            let distanceFromMyPos
            if (searchLatLng) {
              distanceFromMyPos = calcCrow(searchLatLng?.lat, searchLatLng?.lng, each.lat, each.lng)
            } else {
              distanceFromMyPos = calcCrow(myPos?.lat, myPos?.lng, each.lat, each.lng)
            }
            return {
              ...each,
              distanceFromMyPos,
            }
          })
          .sort((a, b) => {
            return a.distanceFromMyPos - b.distanceFromMyPos
          })

        setMarkerInBounds(tmp)
      }
    }, 500),
    [searchLatLng, myPos]
  )

  const handleActiveMarker = (marker) => {
    if (marker === activeMarker) {
      return
    }
    setActiveMarker(marker)
    const markerInBounds = MARKERS.find((each) => each.lat === marker)

    const latlngbounds = mapRef?.current?.getBounds()
    const inBounds = latlngbounds?.contains({ lat: markerInBounds.lat, lng: markerInBounds.lng })

    if (!inBounds && mapRef.current) {
      mapRef.current.setCenter({
        lat: markerInBounds.lat,
        lng: markerInBounds.lng,
      })
      mapRef.current.setZoom(15)
    }
  }

  // useEffect(() => {
  //   // Try HTML5 geolocation.
  //   if (navigator.geolocation) {
  //     navigator.geolocation.getCurrentPosition((position) => {
  //       const pos = {
  //         lat: position.coords.latitude,
  //         lng: position.coords.longitude,
  //       }
  //       setMyPos(pos)

  //       mapRef?.current?.setCenter?.(pos)
  //     })
  //   } else {
  //     // Browser doesn't support Geolocation
  //     console.log('Error')
  //   }
  // }, [])

  const handleLocateMe = () => {
    // Try HTML5 geolocation.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          setMyPos(pos)
          setSearchLatLng(null)
          const input = document.getElementById('textfield')
          if (input) input.value = ''

          mapRef?.current?.setCenter?.(pos)
        }
        // (err) => {
        //   console.log(err)
        // }
      )
    } else {
      // Browser doesn't support Geolocation
      console.log('Error')
    }
  }

  if (!isLoaded) return null

  return (
    <Box display='flex' width='100%' height='100%' justifyContent='center'>
      <Box
        width='100%'
        maxWidth={270}
        px={5}
        py={3}
        display='flex'
        flexDirection='column'
        alignItems='center'
      >
        <Box py={2} width='100%'>
          <Button
            variant='outlined'
            fullWidth
            startIcon={<LocationSearchingIcon />}
            onClick={handleLocateMe}
          >
            Locate Me
          </Button>
        </Box>
        <StyledAutocomplete
          onLoad={onLoadAutoComplete}
          onPlaceChanged={onPlaceChanged}
          options={{ componentRestrictions: { country: 'MY' } }}
        >
          <TextField
            placeholder='Search city or postcode'
            label='Search'
            fullWidth
            id='textfield'
          />
        </StyledAutocomplete>

        <Box display='flex' alignItems='center' flexDirection='column' width='100%' overflow='auto'>
          {markerInBounds && markerInBounds?.length > 0 ? (
            markerInBounds?.map((each) => {
              const handleOnClick = () => {
                handleActiveMarker(each.lat)
              }
              return (
                <Box
                  boxSizing='border-box'
                  width='100%'
                  style={{ marginBottom: '16px' }}
                  key={each.lat}
                  onClick={handleOnClick}
                >
                  <Card>
                    <CardContent>
                      {each?.distanceFromMyPos ? (
                        <Box display='flex' alignItems='center' mb={1}>
                          <LocationOnOutlinedIcon />

                          <Typography sx={{ fontSize: 16 }} color='text.secondary'>
                            {each?.distanceFromMyPos?.toFixed(1)}km
                          </Typography>
                        </Box>
                      ) : null}

                      <Typography sx={{ fontSize: 16 }} color='text.secondary' gutterBottom>
                        {each?.dealer}
                      </Typography>
                      <Typography color='text.secondary' sx={{ fontSize: 14 }}>
                        Contact
                      </Typography>
                      <Typography variant='body2' sx={{ mb: 1.5 }}>
                        {each?.phone}
                      </Typography>
                      <Typography color='text.secondary' sx={{ fontSize: 14 }}>
                        Address
                      </Typography>
                      <Typography variant='body2'>{each?.address}</Typography>
                    </CardContent>
                  </Card>
                </Box>
              )
            })
          ) : (
            <Box mt={3}>
              <Typography>No result found</Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        width='100%'
        height='100%'
        visibility={{ xs: 'hidden', sm: 'visible' }}
        position={{ xs: 'absolute', sm: 'unset' }}
      >
        <GoogleMap
          id='searchbox-example'
          onBoundsChanged={onBoundsChanged}
          mapContainerStyle={{
            height: '100%',
            width: '100%',
          }}
          zoom={activeMarker ? 15 : 12}
          center={center}
          onLoad={onLoadMap}
          options={{ mapTypeControl: false, streetViewControl: false, styles: mapStyle }}
        >
          {MARKERS.map(({ lat, lng, dealer, address, link }, index) => (
            <Marker
              key={index}
              position={{ lat, lng }}
              onClick={() => handleActiveMarker(lat)}
              icon={{ url: `/client-static/images/fmdgoodicon.png` }}
            >
              {activeMarker === lat ? (
                <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                  <div>
                    <b>{dealer}</b>
                    <br />
                    <br />
                    {address}
                    <br />
                    <br />
                    <a href={link} target='_blank'>
                      Get Directions
                    </a>
                  </div>
                </InfoWindow>
              ) : null}
            </Marker>
          ))}
        </GoogleMap>
      </Box>
    </Box>
  )
}

const StyledAutocomplete = styled(Autocomplete)`
  && {
    width: 100%;
  }
`

const mapStyle = [
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e9e9e9',
      },
      {
        lightness: 17,
      },
    ],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [
      {
        color: '#f5f5f5',
      },
      {
        lightness: 20,
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ffffff',
      },
      {
        lightness: 17,
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [
      {
        color: '#ffffff',
      },
      {
        lightness: 29,
      },
      {
        weight: 0.2,
      },
    ],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff',
      },
      {
        lightness: 18,
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff',
      },
      {
        lightness: 16,
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [
      {
        color: '#f5f5f5',
      },
      {
        lightness: 21,
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [
      {
        color: '#dedede',
      },
      {
        lightness: 21,
      },
    ],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [
      {
        visibility: 'on',
      },
      {
        color: '#ffffff',
      },
      {
        lightness: 16,
      },
    ],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [
      {
        saturation: 36,
      },
      {
        color: '#333333',
      },
      {
        lightness: 40,
      },
    ],
  },
  {
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [
      {
        color: '#f2f2f2',
      },
      {
        lightness: 19,
      },
    ],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#fefefe',
      },
      {
        lightness: 20,
      },
    ],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [
      {
        color: '#fefefe',
      },
      {
        lightness: 17,
      },
      {
        weight: 1.2,
      },
    ],
  },
]
